<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  Bell,
  CalendarClock,
  Check,
  MessageSquareText,
  Settings2,
  UserRoundPlus,
  X,
} from "lucide-vue-next";
import { useRouter } from "vue-router";
import { useWorkspaceStore } from "@/stores/workspace";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();
const workspace = useWorkspaceStore();
const router = useRouter();
const preferences = ref(false);
const digest = ref<"immediate" | "daily" | "weekly" | "off">("daily");
const preferenceLoading = ref(false);
const preferenceSaving = ref(false);
const preferenceError = ref("");
const drawer = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
let returnFocus: HTMLElement | null = null;
let priorOverflow = "";
const items = computed(() => workspace.notifications);
const icon = {
  mention: MessageSquareText,
  schedule: CalendarClock,
  assignment: UserRoundPlus,
  risk: Bell,
};

function openItem(id: string, taskId?: string) {
  workspace.markNotification(id);
  emit("close");
  if (taskId)
    router.push({
      path: "/projects/prj_demo0001/list",
      query: { task: taskId },
    });
}

async function openPreferences() {
  preferences.value = true;
  preferenceError.value = "";
  preferenceLoading.value = true;
  try {
    const current = await workspace.loadNotificationPreferences();
    digest.value =
      current.digest !== "off"
        ? current.digest
        : current.immediate
          ? "immediate"
          : "off";
  } catch {
    preferenceError.value =
      "We could not load your saved preferences. Your current delivery settings have not changed. Try again.";
  } finally {
    preferenceLoading.value = false;
  }
}

async function savePreferences() {
  preferenceSaving.value = true;
  preferenceError.value = "";
  try {
    await workspace.saveNotificationPreferences({
      immediate: digest.value === "immediate",
      digest:
        digest.value === "daily" || digest.value === "weekly"
          ? digest.value
          : "off",
    });
    preferences.value = false;
    emit("close");
  } catch {
    preferenceError.value =
      "We could not save your preferences. Your choices are still here. Try again.";
  } finally {
    preferenceSaving.value = false;
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      returnFocus = document.activeElement as HTMLElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      closeButton.value?.focus();
    } else {
      document.body.style.overflow = priorOverflow;
      returnFocus?.focus();
    }
  },
);
onBeforeUnmount(() => {
  document.body.style.overflow = priorOverflow;
});

function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab" || !drawer.value) return;
  const items = [
    ...drawer.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!items.length) return;
  const first = items[0]!;
  const last = items.at(-1)!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer">
      <div v-if="open" class="drawer-layer">
        <button
          class="drawer-scrim"
          type="button"
          aria-label="Close notifications"
          @click="emit('close')"
        ></button>
        <section
          ref="drawer"
          class="drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          @keydown="trapKeys"
        >
          <header class="drawer__header">
            <div>
              <h2 id="notice-title">
                {{ preferences ? "Notification preferences" : "Notifications" }}
              </h2>
              <p>
                {{
                  preferences
                    ? "Choose how routine project updates reach you."
                    : "Changes that may need your attention."
                }}
              </p>
            </div>
            <button
              ref="closeButton"
              class="icon-button"
              type="button"
              aria-label="Close notifications"
              @click="emit('close')"
            >
              <X :size="20" />
            </button>
          </header>
          <div class="drawer__body">
            <template v-if="!preferences">
              <button
                v-for="item in items"
                :key="item.id"
                type="button"
                class="notice"
                :class="{ 'notice--unread': !item.read }"
                @click="openItem(item.id, item.taskId)"
              >
                <span class="notice__icon"
                  ><component :is="icon[item.kind]" :size="17"
                /></span>
                <span class="notice__copy"
                  ><strong>{{ item.title }}</strong
                  ><span>{{ item.body }}</span
                  ><small
                    >{{ item.projectName }} · {{ item.timestamp }}</small
                  ></span
                >
                <span v-if="item.read" class="notice__read"
                  ><Check :size="14" aria-label="Read"
                /></span>
              </button>
              <div v-if="!items.length" class="empty-notices">
                <Bell :size="25" /><strong>You are all caught up.</strong
                ><span
                  >New assignments, mentions, and schedule changes will appear
                  here.</span
                >
              </div>
            </template>
            <form v-else class="preferences" @submit.prevent="savePreferences">
              <fieldset :disabled="preferenceLoading || preferenceSaving">
                <legend>Routine project updates</legend>
                <label
                  ><input v-model="digest" type="radio" value="immediate" />
                  Immediately</label
                >
                <label
                  ><input v-model="digest" type="radio" value="daily" /> Daily
                  digest <span>8:00 AM in your timezone</span></label
                >
                <label
                  ><input v-model="digest" type="radio" value="weekly" /> Weekly
                  digest <span>Mondays at 8:00 AM</span></label
                >
                <label
                  ><input v-model="digest" type="radio" value="off" />
                  Off</label
                >
              </fieldset>
              <div class="preference-note">
                Mentions, invitations, access changes, and material changes to
                work you own are still sent immediately.
              </div>
              <p v-if="preferenceError" class="form-error" role="alert">
                {{ preferenceError }}
              </p>
              <p v-else-if="preferenceLoading" class="muted" role="status">
                Loading preferences…
              </p>
            </form>
          </div>
          <footer class="drawer__footer">
            <button
              v-if="!preferences"
              class="button button--secondary"
              type="button"
              @click="openPreferences"
            >
              <Settings2 :size="16" /> Preferences
            </button>
            <template v-else
              ><button
                class="button button--secondary"
                type="button"
                :disabled="preferenceSaving"
                @click="preferences = false"
              >
                Cancel</button
              ><button
                class="button button--primary"
                type="button"
                :disabled="preferenceLoading || preferenceSaving"
                @click="savePreferences"
              >
                {{ preferenceSaving ? "Saving…" : "Save preferences" }}
              </button></template
            >
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.drawer-layer {
  position: fixed;
  inset: 0;
  z-index: 100;
}
.drawer-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.36);
}
.drawer {
  position: absolute;
  inset: 0 0 0 auto;
  width: min(440px, 100vw);
  display: grid;
  grid-template-rows: auto 1fr auto;
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.drawer__header {
  min-height: 86px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--rule);
}
.drawer__header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.drawer__body {
  overflow: auto;
}
.drawer__footer {
  min-height: 64px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 13px 20px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.notice {
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 34px 1fr auto;
  gap: 11px;
  padding: 15px 18px;
  border-bottom: 1px solid var(--rule);
  background: white;
  text-align: left;
  cursor: pointer;
}
.notice:hover {
  background: var(--sheet-subtle);
}
.notice--unread {
  background: #f4faf7;
}
.notice--unread::before {
  content: "";
  position: absolute;
  left: 0;
  top: 13px;
  bottom: 13px;
  width: 2px;
  background: var(--brand);
}
.notice__icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--sheet-strong);
  color: var(--ink-800);
}
.notice__copy {
  display: grid;
  gap: 3px;
}
.notice__copy > span {
  color: var(--text-soft);
}
.notice__copy small {
  margin-top: 2px;
  color: var(--text-faint);
}
.notice__read {
  color: var(--success);
}
.empty-notices {
  min-height: 300px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 8px;
  padding: 30px;
  color: var(--text-soft);
  text-align: center;
}
.empty-notices strong {
  color: var(--text);
}
.preferences {
  padding: 22px;
}
.preferences fieldset {
  display: grid;
  gap: 10px;
  padding: 0;
  border: 0;
}
.preferences legend {
  margin-bottom: 12px;
  font-weight: 700;
}
.preferences label {
  min-height: 46px;
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  cursor: pointer;
}
.preferences label:has(input:checked) {
  border-color: var(--brand);
  background: var(--brand-soft);
}
.preferences label span {
  grid-column: 2;
  color: var(--text-soft);
  font-size: 12px;
}
.preference-note {
  margin-top: 18px;
  padding: 12px;
  border-radius: 6px;
  color: #554a2f;
  background: var(--warning-soft);
}
.form-error {
  color: var(--danger);
  font-weight: 650;
}
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 180ms var(--ease);
}
.drawer-enter-active .drawer,
.drawer-leave-active .drawer {
  transition: transform 180ms var(--ease);
}
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter-from .drawer,
.drawer-leave-to .drawer {
  transform: translateX(24px);
}
</style>
